import { useCallback } from "react"
import { useMutation } from "@tanstack/react-query"
import { AxiosInstance } from "axios"
import { useAuthedAxios } from "hooks/core/useAuthedAxios"

/**
 * Imperative command wrapper: `const [run, isPending] = useApiCommand(apiFn)`.
 *
 * The replacement for the legacy `useApiCall` — same call-site ergonomics,
 * but the auth flows through `useAuthedAxios` (the app's single auth bridge)
 * and execution runs as a react-query mutation (isPending, devtools
 * visibility, standard error semantics — rejections propagate to the
 * caller).
 *
 * Use this for command-style operations (run/convert/export/poll) where the
 * caller needs the response inline. Cacheable server-state reads should be
 * `useQuery` hooks instead.
 */
export const useApiCommand = <TParams extends unknown[], TResponse>(
  apiCall: (
    axiosInstance: AxiosInstance,
    ...params: TParams
  ) => Promise<TResponse>
): [(...params: TParams) => Promise<TResponse>, boolean] => {
  const getAxios = useAuthedAxios()

  const mutation = useMutation({
    mutationFn: async (params: TParams): Promise<TResponse> =>
      apiCall(await getAxios(), ...params),
  })

  const { mutateAsync } = mutation
  const run = useCallback(
    (...params: TParams): Promise<TResponse> => mutateAsync(params),
    [mutateAsync]
  )

  return [run, mutation.isPending]
}
